import { ChevronDown, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { ShiftExcludedCaregiversSummary } from '@apps/web/components/outreaches/inhouse_recommendations_table';
import {
    getNumberValue,
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import {
    buildCaregiverSignalMatcher,
    CAREGIVER_MEMORY_CARD_LIMIT,
    CaregiverNameButton,
    CaregiverSignalChips,
    CaregiverSignalEvidenceSection,
    uniqueCaregiverSignalValues,
} from '@apps/web/components/phoebe_chat_view/tool_calls/caregiver_memory_tool_ui';
import { SearchResultsHeader } from '@apps/web/components/phoebe_chat_view/tool_calls/search_tool_ui';
import {
    formatCaregiverSignalLabel,
    getStringArray,
    TOOL_SURFACE_HOVER_CLASS,
    ToolToken,
    ToolTopicSection,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { Badge } from '@apps/web/components/ui/badge';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { cn } from '@apps/web/lib/utils';

function formatRecommendationAvailability(value: string | null): string | null {
    if (!value) {
        return null;
    }

    if (value === 'has_shift_conflict') {
        return 'Schedule Conflict';
    }
    if (value === 'available') {
        return 'Available';
    }
    return formatCaregiverSignalLabel(value);
}

export function RecommendationFitCards({
    data,
    onCaregiverClick,
}: {
    readonly data: Record<string, unknown>;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const [showAllRecommendations, setShowAllRecommendations] = useState(false);
    const recommendations = Array.isArray(data['recommendations'])
        ? data['recommendations'].filter(isRecord)
        : [];
    const count =
        getNumberValue(data, 'count') ??
        getNumberValue(data, 'recommendation_count') ??
        recommendations.length;
    const shift = isRecord(data['shift']) ? data['shift'] : null;
    const shifts = Array.isArray(data['shifts'])
        ? data['shifts'].filter(isRecord)
        : [];
    const appliedSettings = isRecord(data['applied_settings'])
        ? data['applied_settings']
        : null;
    const groupLabel =
        getStringValue(data, 'group_label') ??
        getStringValue(data, 'display_label');
    const groupMode = getStringValue(data, 'group_mode');
    const patientName = getStringValue(shift, 'patient_name');
    const date = getStringValue(shift, 'date');
    const start = getStringValue(shift, 'start');
    const shiftLabel =
        patientName || date || start
            ? `${patientName ?? 'Shift'}${date ? ` • ${date}` : ''}${start ? ` at ${start}` : ''}`
            : null;
    const visibleRecommendations = showAllRecommendations
        ? recommendations
        : recommendations.slice(0, CAREGIVER_MEMORY_CARD_LIMIT);
    const hiddenRecommendationCount =
        recommendations.length - visibleRecommendations.length;
    return (
        <div className="space-y-2">
            <div className="border-border/60 bg-muted/30 rounded-lg border px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                        <p className="text-foreground text-sm font-semibold">
                            {count === 0
                                ? 'No recommendations found'
                                : `${count} recommendation${count === 1 ? '' : 's'} found`}
                        </p>
                        {groupLabel ? (
                            <p className="text-muted-foreground mt-0.5 text-[11px]">
                                {groupLabel}
                            </p>
                        ) : shiftLabel ? (
                            <p className="text-muted-foreground mt-0.5 text-[11px]">
                                {shiftLabel}
                            </p>
                        ) : null}
                    </div>
                    <div className="flex flex-wrap justify-end gap-1">
                        {shifts.length > 0 ? (
                            <ToolToken>
                                {shifts.length} shift
                                {shifts.length === 1 ? '' : 's'}
                            </ToolToken>
                        ) : null}
                        {groupMode ? (
                            <ToolToken>
                                {formatCaregiverSignalLabel(groupMode)}
                            </ToolToken>
                        ) : null}
                    </div>
                </div>
            </div>
            {visibleRecommendations.map((recommendation, index) => {
                const caregiverId = getStringValue(
                    recommendation,
                    'caregiver_id',
                );
                const caregiverName =
                    getStringValue(recommendation, 'caregiver_name') ??
                    'Caregiver';
                const rank =
                    getNumberValue(recommendation, 'rank') ?? index + 1;
                const distance =
                    getNumberValue(recommendation, 'distance') ??
                    getNumberValue(recommendation, 'distance_miles');
                const starRating = getNumberValue(
                    recommendation,
                    'star_rating',
                );
                const isOvertime =
                    getStringValue(recommendation, 'overtime_status') ===
                    'overtime';
                const scheduleAvailability = getStringValue(
                    recommendation,
                    'schedule_availability',
                );
                const scheduleLabel =
                    formatRecommendationAvailability(scheduleAvailability);
                const hasScheduleConflict =
                    scheduleAvailability === 'has_shift_conflict';
                const coveredShiftIds = getStringArray(
                    recommendation['covered_shift_ids'],
                );
                const uncoveredShiftIds = getStringArray(
                    recommendation['uncovered_shift_ids'],
                );
                const totalShiftCount =
                    shifts.length > 0
                        ? shifts.length
                        : coveredShiftIds.length + uncoveredShiftIds.length;
                const reason =
                    getStringValue(recommendation, 'reasoning') ??
                    getStringValue(recommendation, 'recommendation_reason') ??
                    getStringValue(recommendation, 'fit_summary');
                const signalValues = uniqueCaregiverSignalValues([
                    ...getStringArray(recommendation['roles']),
                    ...getStringArray(recommendation['caregiver_tags']),
                    ...getStringArray(recommendation['tags_matches']),
                ]);
                const matcher = buildCaregiverSignalMatcher(
                    shift?.['skills_required'],
                    shift?.['patient_tags'],
                    appliedSettings?.['patient_tags'],
                    recommendation['tags_matches'],
                );

                return (
                    <div
                        key={caregiverId ?? `${caregiverName}-${rank}`}
                        className={cn(
                            'border-border/60 bg-card rounded-lg border p-3',
                            TOOL_SURFACE_HOVER_CLASS,
                        )}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-muted-foreground text-[10px]">
                                        #{rank}
                                    </span>
                                    <CaregiverNameButton
                                        caregiverId={caregiverId}
                                        name={caregiverName}
                                        onCaregiverClick={onCaregiverClick}
                                        className="text-sm font-semibold"
                                    />
                                </div>
                                <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-1.5 text-[11px]">
                                    {distance !== null ? (
                                        <span>{distance.toFixed(1)} mi</span>
                                    ) : null}
                                    {starRating !== null ? (
                                        <span>
                                            {starRating.toFixed(1)} stars
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <div className="flex flex-wrap justify-end gap-1">
                                {scheduleLabel ? (
                                    <ToolToken
                                        tone={
                                            hasScheduleConflict
                                                ? 'red'
                                                : 'green'
                                        }
                                    >
                                        {scheduleLabel}
                                    </ToolToken>
                                ) : null}
                                {isOvertime ? (
                                    <ToolToken tone="red">Overtime</ToolToken>
                                ) : null}
                                {totalShiftCount > 0 ? (
                                    <ToolToken tone="blue">
                                        {coveredShiftIds.length} of{' '}
                                        {totalShiftCount} shifts covered
                                    </ToolToken>
                                ) : null}
                                {uncoveredShiftIds.length > 0 ? (
                                    <ToolToken tone="red">
                                        {uncoveredShiftIds.length} shift
                                        {uncoveredShiftIds.length === 1
                                            ? ''
                                            : 's'}{' '}
                                        uncovered
                                    </ToolToken>
                                ) : null}
                            </div>
                        </div>
                        {signalValues.length > 0 ? (
                            <div className="mt-3">
                                <CaregiverSignalChips
                                    values={signalValues}
                                    matcher={matcher}
                                />
                            </div>
                        ) : null}
                        {reason ? (
                            <ToolTopicSection
                                title="Reason"
                                className="bg-muted/20 mt-3"
                            >
                                <p className="text-foreground text-xs">
                                    {reason}
                                </p>
                            </ToolTopicSection>
                        ) : null}
                    </div>
                );
            })}
            {hiddenRecommendationCount > 0 ? (
                <button
                    type="button"
                    onClick={() => setShowAllRecommendations(true)}
                    className="text-primary hover:text-primary/80 cursor-pointer text-left text-[10px] font-medium"
                >
                    +{hiddenRecommendationCount} more recommendations
                </button>
            ) : null}
        </div>
    );
}

function getRecommendationDetailResults(
    data: Record<string, unknown>,
): Array<Record<string, unknown>> {
    if (Array.isArray(data['results'])) {
        return data['results'].filter(isRecord);
    }
    return getStringValue(data, 'caregiver_id') ? [data] : [];
}

function getFullListSizeForShift(
    result: Record<string, unknown>,
    shiftId: string | null,
): number | null {
    const directSize =
        getNumberValue(result, 'full_list_size') ??
        getNumberValue(result, 'full_list_count');
    if (directSize !== null) {
        return directSize;
    }
    const fullListSizes = isRecord(result['full_list_sizes'])
        ? result['full_list_sizes']
        : null;
    if (shiftId === null || fullListSizes === null) {
        return null;
    }
    const size = fullListSizes[shiftId];
    return typeof size === 'number' && Number.isFinite(size) ? size : null;
}

export function RecommendationDetailCards({
    data,
    onCaregiverClick,
}: {
    readonly data: Record<string, unknown>;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const results = getRecommendationDetailResults(data);
    const foundCount =
        getNumberValue(data, 'found_count') ??
        results.filter((result) => result['found'] !== false).length;

    return (
        <div className="space-y-2">
            <SearchResultsHeader
                countLabel={`${foundCount} caregiver${foundCount === 1 ? '' : 's'} checked`}
            />
            {results.map((result, resultIndex) => {
                const caregiverId = getStringValue(result, 'caregiver_id');
                const caregiverName =
                    getStringValue(result, 'caregiver_name') ?? 'Caregiver';
                const starRating = getNumberValue(result, 'star_rating');
                const officeName = getStringValue(result, 'office_name');
                const resultFound = result['found'] !== false;
                const reason = getStringValue(result, 'reason');
                const fitSummary = getStringValue(result, 'fit_summary');
                const perShift = Array.isArray(result['per_shift'])
                    ? result['per_shift'].filter(isRecord)
                    : [];
                const coveredShiftIds = getStringArray(
                    result['covered_shift_ids'],
                );
                const uncoveredShiftIds = getStringArray(
                    result['uncovered_shift_ids'],
                );
                const signalValues = uniqueCaregiverSignalValues([
                    ...getStringArray(result['roles']),
                    ...getStringArray(result['caregiver_tags']),
                    ...getStringArray(result['matched_signals']),
                    ...getStringArray(result['conflicting_signals']),
                    ...getStringArray(result['conflicts']),
                ]);
                const matcher = buildCaregiverSignalMatcher(
                    {
                        tags_matches: result['tags_matches'],
                        matched_signals: result['matched_signals'],
                        per_shift_tags_matches: perShift.map(
                            (shiftResult) => shiftResult['tags_matches'],
                        ),
                        conflicting_signals: result['conflicting_signals'],
                        conflicts: result['conflicts'],
                    },
                    fitSummary,
                );

                return (
                    <div
                        key={
                            caregiverId ??
                            `${caregiverName}-${String(resultIndex)}`
                        }
                        className={cn(
                            'border-border/60 bg-card rounded-lg border p-3',
                            TOOL_SURFACE_HOVER_CLASS,
                        )}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <CaregiverNameButton
                                    caregiverId={caregiverId}
                                    name={caregiverName}
                                    onCaregiverClick={onCaregiverClick}
                                    className="text-sm font-semibold"
                                />
                                <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-1.5 text-[11px]">
                                    {officeName ? (
                                        <span>{officeName}</span>
                                    ) : null}
                                    {starRating !== null ? (
                                        <span>
                                            {starRating.toFixed(1)} stars
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <ToolToken tone={resultFound ? 'green' : 'red'}>
                                {resultFound ? 'Found' : 'Missing'}
                            </ToolToken>
                        </div>
                        {fitSummary ? (
                            <ToolTopicSection
                                title="Fit"
                                className="bg-muted/20 mt-3"
                            >
                                <p className="text-foreground text-xs">
                                    {fitSummary}
                                </p>
                            </ToolTopicSection>
                        ) : null}
                        {reason ? (
                            <p
                                className={cn(
                                    'mt-2 text-xs',
                                    resultFound
                                        ? 'text-muted-foreground'
                                        : 'text-destructive',
                                )}
                            >
                                {reason}
                            </p>
                        ) : null}
                        {signalValues.length > 0 ? (
                            <CaregiverSignalEvidenceSection
                                values={signalValues}
                                matcher={matcher}
                                className="bg-muted/20 mt-3"
                            />
                        ) : null}
                        {coveredShiftIds.length > 0 ||
                        uncoveredShiftIds.length > 0 ? (
                            <div className="mt-3 flex flex-wrap gap-1">
                                {coveredShiftIds.length > 0 ? (
                                    <ToolToken tone="blue">
                                        {coveredShiftIds.length} covered shift
                                        {coveredShiftIds.length === 1
                                            ? ''
                                            : 's'}
                                    </ToolToken>
                                ) : null}
                                {uncoveredShiftIds.length > 0 ? (
                                    <ToolToken tone="red">
                                        {uncoveredShiftIds.length} uncovered
                                    </ToolToken>
                                ) : null}
                            </div>
                        ) : null}
                        {perShift.length > 0 ? (
                            <ToolTopicSection
                                title="Shift evidence"
                                className="bg-muted/20 mt-3"
                            >
                                <div className="space-y-2">
                                    {perShift.map((shiftResult, index) => {
                                        const shiftId = getStringValue(
                                            shiftResult,
                                            'shift_id',
                                        );
                                        const shiftFound =
                                            shiftResult['found'] !== false;
                                        const rank =
                                            getNumberValue(
                                                shiftResult,
                                                'rank_in_full_list',
                                            ) ??
                                            getNumberValue(shiftResult, 'rank');
                                        const fullListSize =
                                            getFullListSizeForShift(
                                                result,
                                                shiftId,
                                            );
                                        const distance =
                                            getNumberValue(
                                                shiftResult,
                                                'distance_miles',
                                            ) ??
                                            getNumberValue(
                                                shiftResult,
                                                'distance',
                                            );
                                        const previousVisits = getNumberValue(
                                            shiftResult,
                                            'previous_visits',
                                        );
                                        const scheduleLabel =
                                            formatRecommendationAvailability(
                                                getStringValue(
                                                    shiftResult,
                                                    'schedule_availability',
                                                ),
                                            );
                                        const shiftReason = getStringValue(
                                            shiftResult,
                                            'reason',
                                        );

                                        return (
                                            <div
                                                key={
                                                    shiftId ??
                                                    `shift-detail-${index}`
                                                }
                                                className="border-border/40 bg-background rounded-md border px-2 py-1.5"
                                            >
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <span className="text-foreground text-xs font-medium">
                                                        {shiftId
                                                            ? `Shift ${index + 1}`
                                                            : 'Shift'}
                                                    </span>
                                                    {rank !== null ? (
                                                        <ToolToken tone="blue">
                                                            Rank #{rank}
                                                            {fullListSize !==
                                                            null
                                                                ? ` of ${fullListSize}`
                                                                : ''}
                                                        </ToolToken>
                                                    ) : null}
                                                    {scheduleLabel ? (
                                                        <ToolToken
                                                            tone={
                                                                shiftFound
                                                                    ? 'green'
                                                                    : 'red'
                                                            }
                                                        >
                                                            {scheduleLabel}
                                                        </ToolToken>
                                                    ) : null}
                                                </div>
                                                <div className="text-muted-foreground mt-1 flex flex-wrap gap-1.5 text-[11px]">
                                                    {distance !== null ? (
                                                        <span>
                                                            {distance.toFixed(
                                                                1,
                                                            )}{' '}
                                                            mi
                                                        </span>
                                                    ) : null}
                                                    {previousVisits !== null ? (
                                                        <span>
                                                            {previousVisits}{' '}
                                                            previous visit
                                                            {previousVisits ===
                                                            1
                                                                ? ''
                                                                : 's'}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                {shiftReason ? (
                                                    <p className="text-muted-foreground mt-1 text-xs">
                                                        {shiftReason}
                                                    </p>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            </ToolTopicSection>
                        ) : null}
                        {getStringArray(result['caveats']).length > 0 ? (
                            <ToolTopicSection
                                title="Caveats"
                                className="bg-muted/20 mt-3"
                            >
                                <div className="space-y-1">
                                    {getStringArray(result['caveats']).map(
                                        (caveat) => (
                                            <p
                                                key={caveat}
                                                className="text-muted-foreground text-xs"
                                            >
                                                {caveat}
                                            </p>
                                        ),
                                    )}
                                </div>
                            </ToolTopicSection>
                        ) : null}
                        {getStringArray(result['evidence']).length > 0 ? (
                            <ToolTopicSection
                                title="Evidence"
                                className="bg-muted/20 mt-3"
                            >
                                <div className="space-y-1">
                                    {getStringArray(result['evidence']).map(
                                        (evidence) => (
                                            <p
                                                key={evidence}
                                                className="text-muted-foreground text-xs"
                                            >
                                                {evidence}
                                            </p>
                                        ),
                                    )}
                                </div>
                            </ToolTopicSection>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

export function AutoDraftGroupingCards({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const groups = Array.isArray(data['groups'])
        ? data['groups'].filter(isRecord)
        : [];
    const count = getNumberValue(data, 'group_count') ?? groups.length;
    const model = getStringValue(data, 'model');

    return (
        <div className="space-y-2">
            <SearchResultsHeader
                countLabel={`${count} auto-draft group${count === 1 ? '' : 's'}`}
                query={model}
            />
            {groups.map((group, index) => {
                const shiftIds = getStringArray(group['shift_ids']);
                const label =
                    getStringValue(group, 'label') ??
                    getStringValue(group, 'group_label') ??
                    `Group ${index + 1}`;
                const reason = getStringValue(group, 'reason');
                const appendToOutreachId = getStringValue(
                    group,
                    'append_to_outreach_id',
                );

                return (
                    <div
                        key={getStringValue(group, 'group_id') ?? label}
                        className={cn(
                            'border-border/60 bg-card rounded-lg border p-3',
                            TOOL_SURFACE_HOVER_CLASS,
                        )}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-foreground text-sm font-semibold">
                                    {label}
                                </p>
                                <p className="text-muted-foreground mt-0.5 text-[11px]">
                                    {shiftIds.length} shift
                                    {shiftIds.length === 1 ? '' : 's'}
                                </p>
                            </div>
                            {appendToOutreachId ? (
                                <ToolToken tone="blue">
                                    Append to existing outreach
                                </ToolToken>
                            ) : null}
                        </div>
                        {reason ? (
                            <ToolTopicSection
                                title="Reason"
                                className="bg-muted/20 mt-3"
                            >
                                <p className="text-foreground text-xs">
                                    {reason}
                                </p>
                            </ToolTopicSection>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

function describeUnitDraft(unit: Record<string, unknown>): string {
    if (typeof unit['display_label'] === 'string') {
        return unit['display_label'];
    }

    const unitContext = isRecord(unit['unit_context'])
        ? unit['unit_context']
        : null;
    const shifts = Array.isArray(unitContext?.['shifts'])
        ? (unitContext['shifts'] as Array<unknown>)
        : [];
    const firstShift = isRecord(shifts[0]) ? shifts[0] : null;

    const role =
        firstShift && typeof firstShift['role_required'] === 'string'
            ? (firstShift['role_required'] as string).toUpperCase()
            : null;

    let dateLabel: string | null = null;
    if (firstShift && typeof firstShift['start_time'] === 'string') {
        const parsed = new Date(firstShift['start_time']);
        if (!Number.isNaN(parsed.getTime())) {
            dateLabel = parsed.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
            });
        }
    }

    const parts: Array<string> = [];
    if (shifts.length > 1) {
        parts.push(`${shifts.length} shifts`);
    }
    if (role) {
        parts.push(role);
    }
    if (dateLabel) {
        parts.push(dateLabel);
    }
    if (parts.length > 0) {
        return parts.join(' · ');
    }

    const unitId =
        typeof unit['unit_id'] === 'string' ? (unit['unit_id'] as string) : '';
    return unitId ? `Unit ${unitId.slice(0, 6)}` : 'Unit';
}

function getUnitDraftSuggestions(
    unit: Record<string, unknown>,
): Array<Record<string, unknown>> {
    if (Array.isArray(unit['suggestions'])) {
        return unit['suggestions'].filter(isRecord);
    }
    if (Array.isArray(unit['selected_suggestions'])) {
        return unit['selected_suggestions'].filter(isRecord);
    }
    return [];
}

function getDraftReasonLines(value: string | null): Array<string> {
    if (!value) {
        return [];
    }
    return value
        .split(/\r?\n/)
        .map((line) => line.trim().replace(/^[-*]\s*/, ''))
        .filter((line) => line.length > 0);
}

export function SuggestionDraftCards({
    data,
    unitDrafts,
}: {
    readonly data: Record<string, unknown>;
    readonly unitDrafts: Array<unknown>;
}) {
    const summary = isRecord(data['summary']) ? data['summary'] : null;
    const totalUnits =
        getNumberValue(summary, 'total_units') ?? unitDrafts.length;
    const selectedSuggestionCount =
        getNumberValue(summary, 'selected_suggestion_count') ??
        (Array.isArray(data['suggestions'])
            ? data['suggestions'].filter(isRecord).length
            : unitDrafts.reduce<number>((total, unit) => {
                  if (!isRecord(unit)) {
                      return total;
                  }
                  return total + getUnitDraftSuggestions(unit).length;
              }, 0));
    const conflictCount = Array.isArray(data['cross_unit_conflicts'])
        ? data['cross_unit_conflicts'].filter(isRecord).length
        : 0;

    return (
        <div className="space-y-2">
            <SearchResultsHeader
                countLabel={`${selectedSuggestionCount} suggestion${
                    selectedSuggestionCount === 1 ? '' : 's'
                } across ${totalUnits} unit${totalUnits === 1 ? '' : 's'}`}
            />
            <SubagentDraftRows unitDrafts={unitDrafts} />
            {conflictCount > 0 ? (
                <ToolTopicSection title="Review needed">
                    <p className="text-destructive text-xs">
                        {conflictCount} cross-unit conflict
                        {conflictCount === 1 ? '' : 's'}
                    </p>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

function SubagentReasoningTrace({
    traceLines,
    isPending,
}: {
    readonly traceLines: ReadonlyArray<string>;
    readonly isPending: boolean;
}) {
    const scrollRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        // While drafting, keep the newest streamed reasoning line in view.
        if (isPending && scrollRef.current !== null) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [traceLines, isPending]);
    if (traceLines.length === 0) {
        return null;
    }
    const lines = (
        <div
            ref={scrollRef}
            className="border-border/70 max-h-32 space-y-0.5 overflow-y-auto border-l pl-2"
        >
            {traceLines.map((trace, traceIndex) => (
                <p
                    key={`${traceIndex}-${trace}`}
                    className="text-muted-foreground text-[11px]"
                >
                    {trace}
                </p>
            ))}
        </div>
    );
    if (isPending) {
        return <div className="mt-1.5">{lines}</div>;
    }
    return (
        <Collapsible defaultOpen={false} className="mt-1.5">
            <CollapsibleTrigger className="text-muted-foreground hover:text-foreground group flex items-center gap-1 text-[11px]">
                <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
                Reasoning
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-1">{lines}</CollapsibleContent>
        </Collapsible>
    );
}

export function SubagentDraftRows({
    unitDrafts,
    tracesByUnitId,
}: {
    readonly unitDrafts: ReadonlyArray<unknown>;
    readonly tracesByUnitId?: ReadonlyMap<string, ReadonlyArray<string>>;
}) {
    return (
        <div className="flex flex-col gap-2">
            {unitDrafts.map((rawUnit, index) => {
                if (!isRecord(rawUnit)) {
                    return null;
                }
                const isPending = rawUnit['__pending'] === true;
                const unitId = getStringValue(rawUnit, 'unit_id');
                const unitType = getStringValue(rawUnit, 'unit_type');
                const suggestions = getUnitDraftSuggestions(rawUnit);
                const suggestionCount =
                    getNumberValue(rawUnit, 'suggestion_count') ??
                    suggestions.length;
                const broadened = rawUnit['broadened'] === true;
                const failed = rawUnit['selected_broadening_step'] === 'failed';
                const unresolvedReason = getStringValue(
                    rawUnit,
                    'unresolved_reason',
                );
                const hints = getStringArray(rawUnit['hints']);
                // FF-gated rollup of candidates dropped (or, in agent
                // score-only flows, failing) hard tag requirements —
                // rendered deterministically so the coordinator sees
                // the "why" in the sidebar even when the model's prose
                // omits it.
                const excludedSummary = isRecord(rawUnit['excluded_summary'])
                    ? rawUnit['excluded_summary']
                    : null;
                const excludedCount = excludedSummary
                    ? (getNumberValue(excludedSummary, 'count') ?? 0)
                    : 0;
                const excludedTags = excludedSummary
                    ? getStringArray(excludedSummary['tags'])
                    : [];
                const selectedBroadeningStep = getStringValue(
                    rawUnit,
                    'selected_broadening_step',
                );
                const streamedTraces = unitId
                    ? (tracesByUnitId?.get(unitId) ?? [])
                    : [];
                const persistedTraces = getStringArray(
                    rawUnit['subagent_traces'],
                );
                const traceLines =
                    streamedTraces.length > 0
                        ? streamedTraces
                        : persistedTraces;

                const dotClass = failed
                    ? 'bg-destructive'
                    : suggestionCount > 0
                      ? broadened
                          ? 'bg-amber-500'
                          : 'bg-emerald-500'
                      : 'bg-muted-foreground/60';

                const statusLabel = failed
                    ? 'Failed'
                    : suggestionCount > 0
                      ? `${suggestionCount} draft option${suggestionCount === 1 ? '' : 's'}`
                      : 'No candidates';

                const statusVariant: 'success' | 'destructive' | 'outline' =
                    failed
                        ? 'destructive'
                        : suggestionCount > 0
                          ? 'success'
                          : 'outline';

                return (
                    <div
                        key={`subagent-${index}`}
                        className="border-border/60 bg-background rounded-md border p-2"
                    >
                        <div className="flex items-center gap-2">
                            {isPending ? (
                                <Loader2 className="text-muted-foreground h-3 w-3 flex-shrink-0 animate-spin" />
                            ) : (
                                <span
                                    className={cn(
                                        'inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full',
                                        dotClass,
                                    )}
                                />
                            )}
                            <span className="text-foreground min-w-0 flex-1 truncate text-xs font-medium">
                                {describeUnitDraft(rawUnit)}
                            </span>
                            {isPending ? (
                                <span className="text-muted-foreground text-[10px] italic">
                                    Drafting…
                                </span>
                            ) : (
                                <Badge
                                    variant={statusVariant}
                                    className="rounded-md px-1.5 py-0 text-[10px]"
                                >
                                    {statusLabel}
                                </Badge>
                            )}
                        </div>
                        {!isPending &&
                        (broadened || selectedBroadeningStep) &&
                        !failed ? (
                            <div className="mt-1">
                                <ToolToken tone="blue">
                                    {selectedBroadeningStep
                                        ? formatCaregiverSignalLabel(
                                              selectedBroadeningStep,
                                          )
                                        : 'Broadened'}
                                </ToolToken>
                            </div>
                        ) : null}
                        {!isPending && unitType !== 'shift_group' && unitId ? (
                            <div className="mt-1 [&>div]:pb-0">
                                <ShiftExcludedCaregiversSummary
                                    shiftId={unitId}
                                />
                            </div>
                        ) : null}
                        {!isPending &&
                        unitType === 'shift_group' &&
                        excludedCount > 0 ? (
                            <p className="text-muted-foreground mt-1 text-xs">
                                {excludedTags.length === 1
                                    ? `${excludedCount} caregiver${excludedCount === 1 ? '' : 's'} excluded — requires ${excludedTags[0]}`
                                    : `${excludedCount} caregiver${excludedCount === 1 ? '' : 's'} excluded by tag requirements`}
                            </p>
                        ) : null}
                        <SubagentReasoningTrace
                            traceLines={traceLines}
                            isPending={isPending}
                        />
                        {suggestions.length > 0 ? (
                            <div className="mt-2 space-y-1.5">
                                {suggestions.slice(0, 3).map((suggestion) => {
                                    const caregiverId = getStringValue(
                                        suggestion,
                                        'caregiver_id',
                                    );
                                    const caregiverName =
                                        getStringValue(
                                            suggestion,
                                            'caregiver_name',
                                        ) ?? 'Caregiver';
                                    const reasonLines = getDraftReasonLines(
                                        getStringValue(
                                            suggestion,
                                            'reasoning',
                                        ) ??
                                            getStringValue(
                                                suggestion,
                                                'reason',
                                            ),
                                    );

                                    return (
                                        <div
                                            key={caregiverId ?? caregiverName}
                                            className="space-y-0.5"
                                        >
                                            <p className="text-foreground text-xs font-medium">
                                                {caregiverName}
                                            </p>
                                            {reasonLines.map((line) => (
                                                <p
                                                    key={line}
                                                    className="text-muted-foreground text-xs"
                                                >
                                                    {line}
                                                </p>
                                            ))}
                                        </div>
                                    );
                                })}
                                {suggestions.length > 3 ? (
                                    <p className="text-muted-foreground text-[10px]">
                                        +{suggestions.length - 3} more
                                        suggestions
                                    </p>
                                ) : null}
                            </div>
                        ) : null}
                        {unresolvedReason ? (
                            <p className="text-muted-foreground mt-2 text-xs">
                                {unresolvedReason}
                            </p>
                        ) : null}
                        {hints.length > 0 ? (
                            <div className="mt-1 space-y-0.5">
                                {hints.slice(0, 2).map((hint) => (
                                    <p
                                        key={hint}
                                        className="text-muted-foreground text-xs"
                                    >
                                        {hint}
                                    </p>
                                ))}
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}
